import AddReactionOutlinedIcon from '@mui/icons-material/AddReactionOutlined';
import { Tooltip } from '@mui/material';
import { serverTimestamp } from 'firebase/firestore';
import React, { MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { ReactionType } from '../../../firebase/firebase.types';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { updateCard } from '../../../redux/trip/trip.slice';
import { TripCard } from '../../../redux/trip/trip.types';
import { selectCurrentUser } from '../../../redux/user/user.selectors';
import { getReactionDescription } from '../../../utils/reaction-labels';
import { getReactionCounts, getReactionEmoji, REACTION_TYPES } from '../../../utils/reaction-utils';
import ActionMenu from '../../Card/ActionMenu/ActionMenu';
import ReactionCount from '../../Card/ReactionCount/ReactionCount';
import './CardDetailsActionBar.scss';

interface CardDetailsActionBarProps {
  card: TripCard;
  tripId: string;
  canEdit?: boolean;
  onRename?: () => void;
  onChangeCoverPhoto?: () => void;
  onDelete?: (event: MouseEvent<EventTarget>) => void;
}

function CardDetailsActionBar({
  card,
  tripId,
  canEdit = false,
  onRename,
  onChangeCoverPhoto,
  onDelete,
}: CardDetailsActionBarProps) {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);
  const [showReactionSelector, setShowReactionSelector] = useState(false);
  const reactionContainerRef = useRef<HTMLDivElement>(null);

  const reactions = card.reactions || {};
  const userCurrentReaction = useMemo(() => {
    return currentUser?.uid && reactions[currentUser.uid] ? reactions[currentUser.uid] : null;
  }, [reactions, currentUser?.uid]);

  // Calculate reaction counts
  const reactionCounts = useMemo(() => getReactionCounts(card), [card]);
  const hasReactions = useMemo(() => {
    return Object.values(reactionCounts).some((count) => count > 0);
  }, [reactionCounts]);

  // const handleBookmarkClick = (e: React.MouseEvent) => {
  //   e.stopPropagation();
  //   if (setSnackbarMessage) {
  //     setSnackbarMessage({
  //       text: 'Bookmark feature coming soon!',
  //       severity: 'info',
  //     });
  //   }
  // };

  const onReactionSelect = (reaction: ReactionType | null) => {
    if (!currentUser?.uid) {
      return;
    }

    const userId = currentUser.uid;
    const now = serverTimestamp();

    if (reaction === null) {
      // Remove user's reaction
      const { [userId]: removed, ...remainingReactions } = reactions;

      dispatch(
        updateCard({
          tripId,
          cardId: card.id,
          field: 'reactions',
          value: remainingReactions,
        })
      );
    } else {
      // Add or update user's reaction
      const updatedReactions = {
        ...reactions,
        [userId]: {
          type: reaction,
          timestamp: now,
          email: currentUser.email,
        },
      };

      dispatch(
        updateCard({
          tripId,
          cardId: card.id,
          field: 'reactions',
          value: updatedReactions,
        })
      );
    }
  };

  const handleReactionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowReactionSelector((prev) => !prev);
  };

  const handleReactionSelect = (reaction: ReactionType, e: React.MouseEvent): void => {
    e.stopPropagation();
    const isCurrentlySelected = userCurrentReaction?.type === reaction;
    onReactionSelect(isCurrentlySelected ? null : reaction);
    setShowReactionSelector(false);
  };

  const isSelected = (reaction: ReactionType) => userCurrentReaction?.type === reaction;

  // Close the reaction selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: Event) => {
      if (
        reactionContainerRef.current &&
        !reactionContainerRef.current.contains(e.target as Node)
      ) {
        setShowReactionSelector(false);
      }
    };

    if (showReactionSelector) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showReactionSelector]);

  return (
    <div className='action-btns-container'>
      {hasReactions && (
        <ReactionCount
          card={card}
          isLarge={true}
          isHovered={true}
          isMobile={false}
          isInline={true}
        />
      )}

      {/* <Tooltip
        title='Bookmark this place'
        arrow
        placement='top'
      >
        <button className='details-icon-btn' onClick={handleBookmarkClick}>
          <BookmarkBorderIcon />
        </button>
      </Tooltip> */}

      <div
        className='reaction-btn-container'
        ref={reactionContainerRef}
      >
        <Tooltip
          title='Add your reaction'
          arrow
          placement='top'
        >
          <button
            type='button'
            className='details-icon-btn'
            onClick={handleReactionClick}
          >
            {userCurrentReaction?.type !== undefined ? (
              <span className='reaction-emoji'>{getReactionEmoji(userCurrentReaction.type)}</span>
            ) : (
              <AddReactionOutlinedIcon />
            )}
          </button>
        </Tooltip>

        {showReactionSelector && (
          <div className='details-reaction-selector'>
            {REACTION_TYPES.map((reactionType) => (
              <Tooltip
                key={reactionType}
                title={getReactionDescription(reactionType)}
                arrow
                placement='top'
              >
                <button
                  type='button'
                  className={`details-reaction-btn ${isSelected(reactionType) ? 'selected' : ''}`}
                  onClick={(e: React.MouseEvent) => handleReactionSelect(reactionType, e)}
                >
                  {getReactionEmoji(reactionType)}
                </button>
              </Tooltip>
            ))}
          </div>
        )}
      </div>

      {canEdit && (
        <ActionMenu
          card={card}
          tripId={tripId}
          canEdit={canEdit}
          onRename={onRename}
          onChangeCoverPhoto={onChangeCoverPhoto}
          onDelete={onDelete}
        />
      )}
    </div>
  );
}

CardDetailsActionBar.displayName = 'CardDetailsActionBar';
export default CardDetailsActionBar;
